import {Injectable} from '@angular/core';
import {ModalService} from '@vendure/admin-ui/core';

import imageCompression from 'browser-image-compression';

@Injectable({
  providedIn: 'root',
})
export class CustomAssetService {
  private defaultOptions = {
    quality: 0.8, // 设置压缩质量（值在0-1之间）
    convertSize: 1024000, // 设置压缩文件大小门槛（1MB，单位：字节）
    maxWidthOrHeight: 1920, // 最大宽度或高度
  };

  constructor(private modal: ModalService) {}

  updateDefaultOptions(newOptions: Partial<typeof this.defaultOptions>) {
    this.defaultOptions = {
      ...this.defaultOptions,
      ...newOptions,
    };
  }

  curConvertSize() {
    return this.defaultOptions.convertSize;
  }

  /**
   * 将 Blob 转换为 File 对象
   */
  private blobToFile(blob: Blob, originalFile: File): File {
    const fileType = blob.type || originalFile.type;
    const fileName = originalFile.name.replace(/\.(png|jpg|jpeg|webp)$/i, fileType.includes('png') ? '.png' : '.jpg');
    return new File([blob], fileName, {type: fileType});
  }

  /**
   * 获取图片尺寸
   */
  private getImageDimensions(file: File): Promise<{width: number; height: number}> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({width: img.width, height: img.height});
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };

      img.src = url;
    });
  }

  // 检查图片文件大小，返回没超过门槛的图片
  async checkSizeAndTip(files: File[]) {
    const isExistOverSize = files.filter(f => {
      return this.isImage(f) && f.size > this.defaultOptions.convertSize!;
    });
    const matchSizeFiles = files.filter(f => {
      return this.isImage(f) ? f.size <= this.defaultOptions.convertSize! : this.isVideo(f);
    });
    if (isExistOverSize?.length) {
      const overNames = isExistOverSize.map((m, i) => {
        return `${i + 1}. ${m.name}`;
      });
      const body = ['以下图片大小超过1MB，有造成小程序加载缓慢风险，建议压缩或更换', ...overNames].join('\n');
      this.modal
        .dialog({
          title: '提示',
          body,
          buttons: [{label: '确认', returnValue: true, type: 'primary'}],
        })
        .subscribe();
    }
    return matchSizeFiles;
  }

  async checkSizeAndCompressImageBeforeUpload(files: File[]) {
    const res = await this.checkFilesSize(files);
    return res;
  }
  async checkFilesSize(files: File[]) {
    const isExistOverSize = files.find(f => f.size > this.defaultOptions.convertSize!);
    // console.log(isExistOverSize);
    if (isExistOverSize) {
      const res = await this.handleFilesArr(files);
      // console.log(res);
      return res;
    } else {
      return files.map(f => ({compressed: f, origin: f}));
    }
  }

  async handleFilesArr(files: File[]) {
    const compressedFiles: {compressed: File; origin: File}[] = [];

    for (const f of files) {
      if (f.size < this.defaultOptions.convertSize!) {
        compressedFiles.push({compressed: f, origin: f});
      } else {
        try {
          // 使用 compressImage 进行压缩（quality=0.8）
          const compressedFile = await this.compressImage(f);
          compressedFiles.push({compressed: compressedFile, origin: f});
        } catch (error) {
          console.error('Image compression failed', error);
          // 压缩失败时，如果原文件不是图片，直接添加；如果是图片，添加原文件
          if (this.isImage(f)) {
            compressedFiles.push({compressed: f, origin: f});
          } else {
            continue;
          }
        }
      }
    }
    return compressedFiles;
  }

  /**
   * 压缩图片（主要方法）
   * 使用 browser-image-compression 进行压缩，固定 quality=0.8
   */
  async compressImage(file: File, quality?: number) {
    const isPng = file.type === 'image/png';
    const compressionQuality = quality ?? this.defaultOptions.quality;

    const compressionOptions = {
      maxWidthOrHeight: this.defaultOptions.maxWidthOrHeight,
      useWebWorker: true,
      fileType: isPng ? 'image/png' : 'image/jpeg',
      initialQuality: compressionQuality,
      alwaysKeepResolution: false,
    };

    const compressedBlob = await imageCompression(file, compressionOptions);
    return this.blobToFile(compressedBlob, file);
  }

  isImage(file: File) {
    return file.type.startsWith('image/');
  }

  isVideo(file: File) {
    return file.type.startsWith('video/');
  }
}
